import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

const ACTIVITY_THROTTLE_MS = (typeof window !== 'undefined' && (window as any).MOCK_SESSION_ID) ? 100 : 30 * 1000;

interface UseActivityTrackingResult {
  lastActivity: number;
  getTimeSinceLastActivity: () => number;
  resetActivity: () => void;
  simulateActivityTime: (timestamp: number) => void;
}

export const useActivityTracking = (enabled: boolean = true): UseActivityTrackingResult => {
  const [lastActivity, setLastActivity] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('lastActivityTime');
      const storedTime = stored ? parseInt(stored, 10) : Date.now();
      const now = Date.now();

      if (stored && (now - storedTime) > 20 * 60 * 1000) {
        localStorage.removeItem('lastActivityTime');
        return now;
      }
      return storedTime;
    }
    return Date.now();
  });

  const throttleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isThrottledRef = useRef(false);

  const recordActivity = useCallback((force: boolean = false) => {
    if (!enabled || (!force && isThrottledRef.current)) return;

    const now = Date.now();
    setLastActivity(now);

    if (typeof window !== 'undefined') {
      localStorage.setItem('lastActivityTime', now.toString());
    }

    isThrottledRef.current = true;
    if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
    }
    throttleTimerRef.current = setTimeout(() => {
      isThrottledRef.current = false;
    }, ACTIVITY_THROTTLE_MS);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = () => recordActivity();
    const handleMouseDown = () => recordActivity();
    const handleKeyDown = () => recordActivity();
    const handleScroll = () => recordActivity();
    const handleCustomActivity = () => recordActivity();

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('mousedown', handleMouseDown, { passive: true });
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('fastrack:activity', handleCustomActivity, { passive: true });

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('fastrack:activity', handleCustomActivity);

      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current);
      }
    };
  }, [enabled, recordActivity]);

  const getTimeSinceLastActivity = useCallback(() => {
    return Date.now() - lastActivity;
  }, [lastActivity]);

  const resetActivity = useCallback(() => {
    recordActivity(true);
  }, [recordActivity]);

  const simulateActivityTime = useCallback((timestamp: number) => {
    setLastActivity(timestamp);
    if (typeof window !== 'undefined') {
      localStorage.setItem('lastActivityTime', timestamp.toString());
    }
  }, []);

  return useMemo(() => ({
    lastActivity,
    getTimeSinceLastActivity,
    resetActivity,
    simulateActivityTime
  }), [lastActivity, getTimeSinceLastActivity, resetActivity, simulateActivityTime]);
};

export default useActivityTracking;
